import React, { useEffect } from 'react';
import styled from 'styled-components';
import DonationAmountAnimation from './DonationAmountAnimation';
import { CheckoutWizardProps } from '../CheckoutWizardInterfaces';
import { webConfig } from '../../../../../utils/webConfig';
import { Donor } from '../../../../../providers/DonorProvider/interfaces';
import { useQueryString } from '../../../../../hooks/useQueryString';
import { useDonorProfile, useGiftSummary } from '../../../../../api';
import { Text } from '../../common/Text/Text';
import ThankYouBanner from '../../../../DownloadBanner/ThankYouBanner';
import ReceiptModal from './ReceiptModal';
import { usePushEvents } from '../../../../../hooks/PushEvents';
import {
    appDownloadEvent,
    thankyouScreenShown,
} from '../../../../../hooks/PushEvents/clevertapEvents';
import { isMobile } from '../../../../GetAppModal';
import SeedModal from './SeedModal';
import ShowReceipt from './ShowReceipt';
import ShowSeed from './ShowSeed';
import {
    ApiHandler,
    ReceiptResponseData,
    SeedOfGoodResponse,
} from '../../../../../api';

type ThankYouWizardProps = CheckoutWizardProps;

const StyledDiv = styled.div`
    text-align: center;
    padding: 20px;
    max-width: 552px;
    margin-left: auto;
    margin-right: auto;
    .thank-you-text {
        margin-bottom: 12px;
    }
    .donee-and-receipt {
        margin-top: 16px;
        filter: drop-shadow(0px 1px 2px #ddd);
    }
    .thank-you-footer {
        margin-top: 41px;
        .thank-you-footer-heading {
            margin-bottom: 24px;
        }
        .social-media-icons {
            display: grid;
            gap: 24px;
            justify-content: center;
            grid-template-columns: 52px 52px 52px 52px;
        }
    }
`;

const pixelId = '0:n0mtnal';
const style = { borderStyle: 'none' };
const pixelUrl = `https://insight.adsrvr.org/track/pxl/?adv=wdfbd5e&ct=${pixelId}&fmt=3`;

export const getPixelUrl = (
    doneeId: string | undefined | null,
    donor: Donor | undefined,
    amount: number | undefined,
) => {
    if (!amount || !doneeId || !webConfig.isProduction) {
        return undefined;
    }
    return `${pixelUrl}&v=${amount}&td1=${donor?.uuid}&td2=${doneeId}`;
};

const ThankYouWizard: React.FC<ThankYouWizardProps> = ({ cart, donee }) => {
    const { donorProfile: donor } = useDonorProfile();
    const { totalGiftsCount } = useGiftSummary();
    const { pushEvent } = usePushEvents();
    const [receiptRequestLoading, setReceiptRequestLoading] =
        React.useState(false);
    const [seedRequestLoading, setSeedRequestLoading] = React.useState(false);
    const [receipt, setReceipt] = React.useState<
        ReceiptResponseData | undefined
    >(undefined);
    const [seedData, setSeedData] = React.useState<
        SeedOfGoodResponse | undefined
    >(undefined);
    const query = useQueryString();
    const totalAmount = cart.receipt?.totalAmount;

    useEffect(() => {
        pushEvent(thankyouScreenShown);
        if (isMobile) {
            pushEvent(appDownloadEvent, {
                location: 'receiptthankyou',
                action: 'shown',
                name: 'thank screen bottom sheet only shown on mobile',
            });
        }
    }, [pushEvent]);

    const makeReceiptRequest = React.useCallback(
        async (receiptId: string | number) => {
            setReceiptRequestLoading(true);
            try {
                const request =
                    await ApiHandler.instance.donor.getReceiptRequest(
                        receiptId,
                    );
                if (request.success) {
                    setReceipt(request.data.response.payload);
                } else {
                    setReceipt(undefined);
                }
            } catch {
                setReceipt(undefined);
            } finally {
                setReceiptRequestLoading(false);
            }
        },
        [],
    );

    const makeSeedRequest = React.useCallback(
        async (receiptId: string | number) => {
            setSeedRequestLoading(true);
            try {
                const request =
                    await ApiHandler.instance.points.getSeedOfGood(receiptId);
                if (request.success) {
                    setSeedData(request.data.response.payload);
                } else {
                    setSeedData(undefined);
                }
            } catch {
                setSeedData(undefined);
            } finally {
                setSeedRequestLoading(false);
            }
        },
        [],
    );

    useEffect(() => {
        if (!cart.receipt || receiptRequestLoading) {
            return;
        }
        makeReceiptRequest(cart.receipt.receiptId);
        makeSeedRequest(cart.receipt.receiptId);
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [makeReceiptRequest, cart.receipt]);

    const pixelImage = React.useMemo(
        () => (
            <img
                height={'1'}
                width={'1'}
                style={style}
                alt={''}
                src={getPixelUrl(
                    query.get('doneeId'),
                    donor?.donor,
                    totalAmount,
                )}
            />
        ),
        [donor?.donor, query, totalAmount],
    );

    const seedsBalance = seedData?.totalBalance || 0;
    const isReceiptBlur = !totalGiftsCount || totalGiftsCount < 3;
    const showSeed = isMobile && !!seedData;
    const showReceipt = !seedRequestLoading && !showSeed;

    return (
        <>
            <StyledDiv>
                <Text
                    variant={'medium'}
                    className={'thank-you-text'}
                >
                    Thank you
                    {donor && donor.donor.name ? `, ${donor.donor.name}` : ''}!
                </Text>
                <Text color={'primarySecondaryContent'}>
                    We appreciate your kind and generous gift.
                </Text>
                <DonationAmountAnimation amount={totalAmount} />
                {showReceipt && (
                    <ShowReceipt
                        receiptData={receipt}
                        isReceiptLoading={receiptRequestLoading}
                        donee={donee}
                        isReceiptBlur={isReceiptBlur}
                    />
                )}
            </StyledDiv>
            {showSeed && (
                <ShowSeed
                    donee={donee}
                    seedsBalance={seedsBalance}
                    seeds={seedData}
                />
            )}
            {pixelImage}
            <ThankYouBanner />
            {showSeed && (
                <SeedModal
                    donee={donee}
                    seedsBalance={seedsBalance}
                    seeds={seedData}
                />
            )}
            {showReceipt && (
                <ReceiptModal
                    donee={donee}
                    receiptData={receipt}
                    isReceiptLoading={receiptRequestLoading}
                    isReceiptBlur={isReceiptBlur}
                />
            )}
        </>
    );
};

export default ThankYouWizard;
